import React from 'react';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, BookOpen, Zap, Trophy, ClipboardList } from 'lucide-react';

const STAGE_ICONS = {
  topic: BookOpen,
  test: ClipboardList,
  exam: Trophy,
};

const STAGE_COLORS = {
  topic: { active: '#2563eb', done: '#22c55e', locked: '#9ca3af', bg: '#eff6ff', border: '#bfdbfe' },
  test:  { active: '#7c3aed', done: '#22c55e', locked: '#9ca3af', bg: '#f5f3ff', border: '#ddd6fe' },
  exam:  { active: '#b45309', done: '#22c55e', locked: '#9ca3af', bg: '#fffbeb', border: '#fde68a' },
};

const MasteryMap = ({ curriculum = [], activeTopic, onSelectTopic }) => {
  return (
    <div className="w-full overflow-x-auto pb-3">
      <div className="flex items-center gap-0 min-w-max px-2">
        {curriculum.map((item, index) => {
          const prevCovered = index === 0 || curriculum[index - 1]?.covered;
          const unlocked = prevCovered || item.covered;
          const isActive = activeTopic?.id === item.id;
          const isDone = Boolean(item.covered);
          const colors = STAGE_COLORS[item.type] || STAGE_COLORS.topic;
          const Icon = isDone ? CheckCircle : STAGE_ICONS[item.type] || BookOpen;

          return (
            <React.Fragment key={item.id}>
              {/* Connector line */}
              {index > 0 && (
                <div
                  className="h-1 w-8 shrink-0 rounded-full"
                  style={{ background: curriculum[index - 1]?.covered ? '#22c55e' : '#e5e7eb' }}
                />
              )}

              {/* Node */}
              <motion.div
                whileHover={unlocked ? { scale: 1.05 } : {}}
                whileTap={unlocked ? { scale: 0.97 } : {}}
                onClick={() => unlocked && !isDone && onSelectTopic && onSelectTopic(item)}
                title={item.title}
                className="relative flex flex-col items-center cursor-pointer shrink-0"
                style={{ width: 80 }}
              >
                {/* Circle */}
                <motion.div
                  animate={isActive ? { y: [0, -4, 0] } : {}}
                  transition={isActive ? { repeat: Infinity, duration: 2, ease: 'easeInOut' } : {}}
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md border-2 transition-all"
                  style={{
                    background: isDone ? '#22c55e' : unlocked ? (isActive ? colors.active : colors.bg) : '#f3f4f6',
                    borderColor: isDone ? '#16a34a' : unlocked ? (isActive ? colors.active : colors.border) : '#d1d5db',
                    opacity: !unlocked ? 0.5 : 1,
                  }}
                >
                  {!unlocked
                    ? <Lock size={20} color="#9ca3af" />
                    : <Icon size={20} color={isDone ? '#fff' : (isActive ? '#fff' : colors.active)} strokeWidth={2.5} />
                  }
                </motion.div>

                {/* Stage number badge */}
                <div
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white shadow"
                  style={{ background: isDone ? '#16a34a' : unlocked ? colors.active : '#9ca3af' }}
                >
                  {index + 1}
                </div>

                {/* Label */}
                <p
                  className="mt-2 text-center text-[10px] font-bold leading-tight truncate w-full px-1"
                  style={{ color: isDone ? '#15803d' : unlocked ? '#1e293b' : '#9ca3af' }}
                >
                  {item.title.length > 12 ? item.title.slice(0, 12) + '…' : item.title}
                </p>

                {/* Type badge */}
                {(item.type === 'test' || item.type === 'exam') && (
                  <span
                    className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full mt-0.5"
                    style={{
                      background: item.type === 'exam' ? '#fef3c7' : '#ede9fe',
                      color: item.type === 'exam' ? '#92400e' : '#5b21b6'
                    }}
                  >
                    {item.type}
                  </span>
                )}

                {/* Mastery score */}
                {isDone && item.mastery_score > 0 && (
                  <span className="text-[9px] font-bold text-green-600">
                    <Zap size={9} className="inline" />{item.mastery_score}%
                  </span>
                )}
              </motion.div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default MasteryMap;
